import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, CheckCircle, XCircle, Plus, Search, Download, Share2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bibliotecaDocumentosService } from '../../shared/services/bibliotecaDocumentosService';
import { adminEmpresaService } from '../../shared/services';
import type { Empresa } from '../../shared/types';

export function AdminBibliotecaEmpresasPage() {
  const { grupoId } = useParams<{ grupoId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState<string>('');
  const [podeDownload, setPodeDownload] = useState(true);
  const [podeCompartilhar, setPodeCompartilhar] = useState(false);

  // Buscar grupo
  const { data: grupo, isLoading: isLoadingGrupo } = useQuery({
    queryKey: ['grupo-documento', grupoId],
    queryFn: () => bibliotecaDocumentosService.getGrupoById(grupoId!),
    enabled: !!grupoId,
  });

  // Buscar empresas vinculadas
  const { data: empresasVinculadas = [], isLoading: isLoadingVinculos } = useQuery({
    queryKey: ['empresas-vinculadas', grupoId],
    queryFn: () => bibliotecaDocumentosService.getEmpresasVinculadas(grupoId!),
    enabled: !!grupoId,
  });

  // Buscar todas as empresas disponíveis
  const { data: empresasDisponiveis = [], isLoading: isLoadingEmpresas } = useQuery({
    queryKey: ['empresas-disponiveis'],
    queryFn: async () => {
      console.log('🔍 [AdminBibliotecaEmpresasPage] Buscando empresas disponíveis...');
      const result = await adminEmpresaService.getEmpresas({ page: 1, ativo: true });
      console.log('✅ [AdminBibliotecaEmpresasPage] Empresas recebidas:', result);
      
      // Se retornar objeto paginado, pegar os items
      if (result && typeof result === 'object' && 'items' in result) {
        return Array.isArray(result.items) ? result.items : [];
      }
      
      // Se retornar array direto
      return Array.isArray(result) ? result : [];
    },
  });

  // Vincular empresa
  const vincularMutation = useMutation({
    mutationFn: (data: { empresaId: string; podeDownload: boolean; podeCompartilhar: boolean }) =>
      bibliotecaDocumentosService.vincularEmpresa(grupoId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas-vinculadas', grupoId] });
      queryClient.invalidateQueries({ queryKey: ['grupos-documentos'] });
      setShowAddModal(false);
      setSelectedEmpresa('');
      setPodeDownload(true);
      setPodeCompartilhar(false);
    },
  });

  // Desvincular empresa
  const desvincularMutation = useMutation({
    mutationFn: (empresaId: string) => bibliotecaDocumentosService.desvincularEmpresa(grupoId!, empresaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas-vinculadas', grupoId] });
      queryClient.invalidateQueries({ queryKey: ['grupos-documentos'] });
    },
  });

  const handleVincular = () => {
    if (!selectedEmpresa) return;
    vincularMutation.mutate({
      empresaId: selectedEmpresa,
      podeDownload,
      podeCompartilhar,
    });
  };

  const handleDesvincular = (empresaId: string) => {
    if (confirm('Tem certeza que deseja desvincular esta empresa?')) {
      desvincularMutation.mutate(empresaId);
    }
  };

  // Filtrar empresas já vinculadas
  const empresasJaVinculadas = new Set(empresasVinculadas.map(v => v.empresaId));
  const empresasParaVincular = empresasDisponiveis.filter(
    (emp: Empresa) => !empresasJaVinculadas.has(emp.id || emp.Id || '')
  );

  // Filtrar empresas vinculadas por busca
  const empresasFiltradas = empresasVinculadas.filter((vinculo) => {
    const empresa = empresasDisponiveis.find(
      (emp: Empresa) => (emp.id || emp.Id) === vinculo.empresaId
    );
    if (!empresa) return false;
    const nome = (empresa.nome || empresa.Nome || '').toLowerCase();
    return nome.includes(searchTerm.toLowerCase());
  });

  if (!grupoId) {
    return (
      <div className="p-8">
        <div className="text-red-600">Grupo não encontrado</div>
      </div>
    );
  }

  if (isLoadingGrupo) {
    return (
      <div className="p-8">
        <div className="animate-pulse">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/bibliotecas')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={20} />
          Voltar para Bibliotecas
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Building className="text-primary-600" size={32} />
              Empresas Vinculadas
            </h1>
            <p className="text-gray-600 mt-2">
              Grupo: {grupo?.icone} {grupo?.nome}
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Vincular Empresa
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Vinculadas</p>
              <p className="text-3xl font-bold text-primary-600">{empresasVinculadas.length}</p>
            </div>
            <Building className="text-primary-200" size={48} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Com Download</p>
              <p className="text-3xl font-bold text-green-600">
                {empresasVinculadas.filter(v => v.podeDownload).length}
              </p>
            </div>
            <Download className="text-green-200" size={48} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Com Compartilhamento</p>
              <p className="text-3xl font-bold text-blue-600">
                {empresasVinculadas.filter(v => v.podeCompartilhar).length}
              </p>
            </div>
            <Share2 className="text-blue-200" size={48} />
          </div>
        </div>
      </div>

      {/* Busca */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar empresa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista de Empresas Vinculadas */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isLoadingVinculos || isLoadingEmpresas ? (
          <div className="p-8 text-center text-gray-500">Carregando empresas...</div>
        ) : empresasFiltradas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa vinculada ainda'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CNPJ
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Download
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Compartilhar
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {empresasFiltradas.map((vinculo) => {
                  const empresa = empresasDisponiveis.find(
                    (emp: Empresa) => (emp.id || emp.Id) === vinculo.empresaId
                  );
                  if (!empresa) return null;

                  return (
                    <tr key={vinculo.empresaId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                            <Building className="text-primary-600" size={20} />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {empresa.nome || empresa.Nome}
                            </div>
                            {(empresa.responsavel || empresa.Responsavel) && (
                              <div className="text-sm text-gray-500">
                                {empresa.responsavel || empresa.Responsavel}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {empresa.cnpj || empresa.Cnpj || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {vinculo.podeDownload ? (
                          <CheckCircle className="inline text-green-600" size={20} />
                        ) : (
                          <XCircle className="inline text-gray-400" size={20} />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {vinculo.podeCompartilhar ? (
                          <CheckCircle className="inline text-blue-600" size={20} />
                        ) : (
                          <XCircle className="inline text-gray-400" size={20} />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDesvincular(vinculo.empresaId)}
                          disabled={desvincularMutation.isPending}
                          className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                        >
                          Desvincular
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Adicionar Empresa */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Vincular Empresa</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa *
                </label>
                <select
                  value={selectedEmpresa}
                  onChange={(e) => setSelectedEmpresa(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecione uma empresa</option>
                  {empresasParaVincular.map((empresa: Empresa) => (
                    <option key={empresa.id || empresa.Id} value={empresa.id || empresa.Id}>
                      {empresa.nome || empresa.Nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={podeDownload}
                    onChange={(e) => setPodeDownload(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Permitir download de documentos</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={podeCompartilhar}
                    onChange={(e) => setPodeCompartilhar(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Permitir compartilhamento</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedEmpresa('');
                  setPodeDownload(true);
                  setPodeCompartilhar(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={vincularMutation.isPending}
              >
                Cancelar
              </button>
              <button
                onClick={handleVincular}
                disabled={!selectedEmpresa || vincularMutation.isPending}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {vincularMutation.isPending ? 'Vinculando...' : 'Vincular'}
              </button>
            </div>

            {vincularMutation.isError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                Erro ao vincular empresa. Tente novamente.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
